import {acceptedFileTypes, useUploadFile} from '@github-ui/comment-box/fileUpload'
import type {RepoSubject} from '@github-ui/comment-box/subject'
import {VALIDATORS} from '@github-ui/entity-validators'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import type {FileType} from '@github-ui/use-unified-file-select'
import {useUnifiedFileSelect} from '@github-ui/use-unified-file-select'
import {AlertIcon, UploadIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {GraphqlFragment, IssueFormElementRef, SharedElementProps} from '../types'
import {prefixMarkdownTitle} from '../utils/markdown-conversion'
import {uniqueIdForElementInternal} from '../utils/session-storage'
import type {UploadElement_input$data, UploadElement_input$key} from './__generated__/UploadElement_input.graphql'
import {ElementWrapper} from './ElementWrapper'
import styles from './UploadElement.module.css'
import type {UploadState} from './UploadFileList'
import {UploadFileList} from './UploadFileList'

export type UploadElementInternalProps = GraphqlFragment<UploadElement_input$data> & {
  type: 'upload'
  index?: number
  ref?: React.Ref<IssueFormElementRef>
  subject: RepoSubject
  accept?: string | null
} & SharedElementProps

type UploadElementProps = {
  elementRef: UploadElement_input$key
  index?: number
  ref?: React.Ref<IssueFormElementRef>
  subject: RepoSubject
} & SharedElementProps

/**
 * Parses a comma-separated list of file extensions and filters them against the global accepted file types.
 * @param accept - Comma-separated list of file extensions (e.g., ".png,.log,.webm")
 * @param globalAcceptedTypes - Array of globally accepted file types from acceptedFileTypes(true)
 * @returns Filtered array of accepted file types, or the global list if no valid extensions are found
 */
function parseAndFilterAcceptedFileTypes(
  accept: string | null | undefined,
  globalAcceptedTypes: FileType[],
): FileType[] {
  if (!accept || accept.trim() === '') {
    return globalAcceptedTypes
  }

  const requestedExtensions = accept
    .split(',')
    .map(ext => ext.trim())
    .filter(ext => ext.length > 0)
    .map(ext => (ext.startsWith('.') ? ext : `.${ext}`))

  const filteredExtensions = requestedExtensions.filter(ext => globalAcceptedTypes.includes(ext as FileType))
  return filteredExtensions.length > 0 ? (filteredExtensions as FileType[]) : globalAcceptedTypes
}

export const UploadElement = ({ref, elementRef, sessionStorageKey, ...props}: UploadElementProps) => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment UploadElement_input on IssueFormElementUpload {
        __id
        itemId: id
        label
        descriptionHTML
        required
        accept
      }
    `,
    elementRef,
  )

  const elementStorageKey = uniqueIdForElementInternal(sessionStorageKey, '', data.label, data.__id)
  return <UploadElementInternal {...data} {...props} type={'upload'} sessionStorageKey={elementStorageKey} ref={ref} />
}

type UploadStateStorage = {
  fileName: string
  fileSize: number
  url: string
}

export const UploadElementInternal = ({
  ref,
  label,
  descriptionHTML,
  required,
  itemId,
  subject,
  index: indexOfFormElement,
  sessionStorageKey,
  onFieldChange,
  accept,
}: UploadElementInternalProps) => {
  const [uploadStatesStorage, setUploadStatesStorage] = useSessionStorage<UploadStateStorage[]>(sessionStorageKey, [])
  const [uploadStates, setUploadStates] = useState<UploadState[]>(() => {
    const safeStorage = Array.isArray(uploadStatesStorage) ? uploadStatesStorage : []
    return safeStorage.map(stored => ({
      file: new File([new Uint8Array(stored.fileSize)], stored.fileName, {type: 'application/octet-stream'}),
      url: stored.url,
    }))
  })
  const [validationResult, setValidationResult] = useState<string | undefined>(undefined)
  const [uploadingCount, setUploadingCount] = useState(0)
  const [uploadError, setUploadError] = useState<string | undefined>(undefined)

  const textValue = uploadStates
    .filter(state => state.url)
    .map(state => `[${state.file.name}](${state.url})`)
    .join('\n')

  const inputRef = useRef<HTMLButtonElement>(null)
  const onUploadFile = useUploadFile(subject.repository.databaseId.toString())

  // Get the global accepted file types and filter based on the accept prop
  const globalAcceptedTypes = acceptedFileTypes(true)
  const filteredAcceptedTypes = parseAndFilterAcceptedFileTypes(accept, globalAcceptedTypes)
  const isPasteEnabled = isFeatureEnabled('issue_form_upload_field_paste')
  const handleValueChange = useCallback(
    async (acceptedFiles: File[], _rejectedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return
      }

      setUploadingCount(prev => prev + acceptedFiles.length)
      setUploadError(undefined)

      for (const file of acceptedFiles) {
        ;(async () => {
          try {
            const result = await onUploadFile(file)

            setUploadStates(prev => {
              const updated = [...prev, {file, url: result.url}]
              const newTextValue = updated
                .filter(state => state.url)
                .map(state => `[${state.file.name}](${state.url})`)
                .join('\n')

              onFieldChange?.(itemId, indexOfFormElement, newTextValue)

              return updated
            })

            setUploadingCount(prev => prev - 1)
          } catch {
            setUploadingCount(prev => prev - 1)
            setUploadError(prev => prev || 'Failed to upload file. Please try again.')
          }
        })()
      }
    },
    [onUploadFile, setUploadStates, onFieldChange, itemId, indexOfFormElement],
  )

  const {clickTargetProps, pasteTargetProps, dropTargetProps} = useUnifiedFileSelect({
    multi: true,
    acceptedFileTypes: filteredAcceptedTypes,
    onSelect: handleValueChange,
  })

  const handleRemoveFile = useCallback(
    (index: number) => {
      setUploadError(undefined)
      setUploadStates(prev => {
        const updated = prev.filter((_, i) => i !== index)
        const newTextValue = updated
          .filter(state => state.url)
          .map(state => `[${state.file.name}](${state.url})`)
          .join('\n')

        onFieldChange?.(itemId, indexOfFormElement, newTextValue)

        return updated
      })
    },
    [onFieldChange, itemId, indexOfFormElement],
  )

  // Sync uploadStates to session storage whenever it changes
  useEffect(() => {
    const completedUploads = uploadStates
      .filter(state => state.url)
      .map(state => ({
        fileName: state.file.name,
        fileSize: state.file.size,
        url: state.url!,
      }))
    setUploadStatesStorage(completedUploads)
  }, [uploadStates, setUploadStatesStorage])

  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      markdown: () => prefixMarkdownTitle(label, textValue),
      validate: () => {
        if (!required) {
          return true
        }

        const hasCompletedUploads = uploadStates.some(uploadState => Boolean(uploadState.url))
        const hasText = textValue.trim() !== ''

        if (!hasCompletedUploads && !hasText) {
          setValidationResult(VALIDATORS.fieldRequired)
          return false
        }

        return true
      },
      reset: () => {
        setUploadStates([])
        setUploadStatesStorage([])
        setValidationResult(undefined)
        setUploadError(undefined)
        onFieldChange?.(itemId, indexOfFormElement, '')
      },
      hasChanges: () => textValue !== '',
      getSessionStorageKey: () => sessionStorageKey,
      type: 'upload' as const,
    }),
    [
      label,
      required,
      sessionStorageKey,
      textValue,
      uploadStates,
      setUploadStates,
      setUploadStatesStorage,
      onFieldChange,
      itemId,
      indexOfFormElement,
    ],
  )

  useEffect(() => {
    // If we had a validation error and the user has fixed it, clear the error
    if (validationResult) {
      const hasCompletedUploads = uploadStates.some(uploadState => Boolean(uploadState.url))
      const hasText = textValue.trim() !== ''

      if (hasCompletedUploads || hasText) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setValidationResult(undefined)
      }
    }
  }, [uploadStates, textValue, validationResult])

  return (
    <ElementWrapper label={label} description={descriptionHTML} required={required} validationResult={validationResult}>
      {({labelId, descriptionIds}) => (
        <>
          {isPasteEnabled ? (
            <Button
              ref={inputRef}
              aria-labelledby={labelId}
              aria-describedby={descriptionIds}
              leadingVisual={UploadIcon}
              className={styles.pasteButton}
              {...clickTargetProps}
              {...pasteTargetProps}
              {...dropTargetProps}
            >
              Paste, drop, or click to add files
            </Button>
          ) : (
            <Button
              ref={inputRef}
              aria-labelledby={labelId}
              aria-describedby={descriptionIds}
              leadingVisual={UploadIcon}
              {...clickTargetProps}
            >
              Upload files
            </Button>
          )}
          {uploadingCount > 0 && (
            <div className={styles.uploadingMessage} aria-live="polite">
              {`Uploading ${uploadingCount} ${uploadingCount === 1 ? 'file' : 'files'}...`}
            </div>
          )}
          <UploadFileList uploadStates={uploadStates} onRemoveFile={handleRemoveFile} />
          {uploadError && (
            <div className={styles.errorMessage} role="alert" aria-live="assertive">
              <AlertIcon aria-hidden="true" />
              <span>{uploadError}</span>
            </div>
          )}
        </>
      )}
    </ElementWrapper>
  )
}
